import { useState, useEffect } from 'react';
import { api } from '../lib/api.jsx';

const cardCls = 'bg-card border border-rule-2 rounded-xl px-5 py-4 mb-4';

const cardTitle = (txt) => (
  <h3 className="text-sm font-bold text-blue-400 uppercase tracking-widest mt-0 mb-3.5">{txt}</h3>
);

const Row = ({ label, value, color }) => (
  <div className="flex justify-between items-center border-b border-rule-2 py-1.5">
    <span className="text-muted text-[13px]">{label}</span>
    <span className="text-[13px] font-semibold font-mono text-fg" style={color ? { color } : undefined}>
      {value}
    </span>
  </div>
);

const StatBox = ({ label, value, color }) => (
  <div className="text-center py-3.5 px-2.5 rounded-lg border border-rule-2 bg-sidebar flex-1 min-w-[100px]">
    <div className="text-2xl font-extrabold text-blue-400" style={color ? { color } : undefined}>
      {value}
    </div>
    <div className="text-[11px] text-muted-2 mt-1">{label}</div>
  </div>
);

// Bloc de commande copiable. Les commandes de déploiement sont longues et se
// recopient mal à la main : un clic vaut mieux qu'une sélection au curseur.
const CmdBlock = ({ cmd }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative mt-1.5 group">
      <code className="block p-2 pr-16 rounded-md bg-sidebar border border-rule font-mono text-[11px] text-fg-2 overflow-x-auto whitespace-pre">
        {cmd}
      </code>
      <button
        onClick={copy}
        title="Copier la commande"
        className="absolute top-1 right-1 rounded px-2 py-1 text-[10px] font-semibold bg-card border border-rule-2 text-muted hover:text-fg-2 cursor-pointer"
      >
        {copied ? 'Copié' : 'Copier'}
      </button>
    </div>
  );
};

const DownloadBtn = ({ label, href, sub }) => (
  <a
    href={href}
    download
    className="flex flex-col gap-1 py-3.5 px-[18px] rounded-lg text-fg min-w-[200px] no-underline bg-sidebar border border-blue-900 hover:bg-blue-950/40 transition-colors"
  >
    <div className="text-sm font-bold text-blue-400">{label}</div>
    <div className="text-[11px] text-muted">{sub}</div>
  </a>
);

export function ConfigPage() {
  const [sysInfo, setSysInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentRelease, setAgentRelease] = useState(null);

  useEffect(() => {
    api
      .get('/platform/config/system')
      .then((d) => {
        setSysInfo(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch('/updates/latest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAgentRelease(d))
      .catch(() => {});
  }, []);

  if (loading) return <div className="text-muted p-10">Chargement...</div>;
  if (!sysInfo) return <div className="text-red-400 p-10">Erreur de chargement</div>;

  const p = sysInfo.platform;
  const db = sysInfo.database;
  const c = sysInfo.counts;
  const ep = sysInfo.endpoints;
  const installer = agentRelease?.installer || {};
  const displayVersion = agentRelease?.version || installer.version;
  // Les paquets Linux vivent dans le même manifeste, sous des clés préfixées
  // (« linux-x64 » contre « x64 ») — cf. AgentLinux/Publish-Linux-Agent.sh.
  const linuxPackages = agentRelease?.architectures || {};
  const hasLinuxAgent = !!(linuxPackages['linux-x64'] || linuxPackages['linux-arm64']);

  // Les commandes sont dérivées de l'origine courante : elles restent correctes
  // quel que soit le nom d'hôte par lequel on consulte le dashboard, et surtout
  // les scripts servis par /install/* y retrouvent cette même URL déjà inscrite —
  // il n'y a donc RIEN à remplacer avant de coller.
  const siteRoot = window.location.origin;
  const apiUrl = siteRoot + '/api';

  // Chemin recommandé : une seule ligne, aucun argument, le code est demandé à
  // l'écran par le script lui-même.
  const cmdWindows = `irm ${siteRoot}/install/windows | iex`;
  const cmdLinux = `curl -fsSL ${siteRoot}/install/linux | sudo sh`;

  // Déploiement de masse : le code est passé en argument, plus aucune question.
  // La forme « scriptblock » est la seule qui permette de passer des paramètres à
  // un script récupéré par irm — « irm … | iex » ne transmet pas d'arguments.
  const cmdWindowsMass = `& ([scriptblock]::Create((irm ${siteRoot}/install/windows))) -Code MH-XXXXXXXX`;
  const cmdLinuxMass = `curl -fsSL ${siteRoot}/install/linux | sudo sh -s -- --code MH-XXXXXXXX`;

  // Repli hors ligne : l'installateur téléchargé à la main, l'URL passée
  // explicitement puisque l'exe ne peut pas la déduire.
  const cmdWindowsExe =
    `start /wait FonxtioAgent-Installer-x64.exe /silent /code MH-XXXXXXXX /url ${apiUrl}\n` +
    `echo Resultat: %ERRORLEVEL%`;
  const cmdLinuxUninstall = `curl -fsSL ${siteRoot}/install/linux | sudo sh -s -- --uninstall`;

  return (
    <div>
      <h1 className="text-[22px] font-bold text-fg mb-5">Configuration & Informations système</h1>

      <div className={cardCls}>
        {cardTitle("Téléchargement de l'agent")}
        <div className="flex gap-3.5 flex-wrap items-center">
          <div className="flex-[1_1_250px]">
            <div className="text-[13px] text-muted mb-1.5">Version disponible</div>
            <div className="text-xl font-bold text-fg">{displayVersion ? 'v' + displayVersion : '—'}</div>
            <div className="text-xs text-muted-2 mt-1.5 max-w-[400px]">
              Installateur WPF auto-extractible — aucune extraction manuelle requise
            </div>
          </div>
          {installer.x64 && (
            <DownloadBtn
              label="Télécharger x64"
              href={'/updates/' + installer.x64.fileName}
              sub={installer.x64.sizeMB + ' MB · Windows 64-bit'}
            />
          )}
          {installer.arm64 && (
            <DownloadBtn
              label="Télécharger ARM64"
              href={'/updates/' + installer.arm64.fileName}
              sub={installer.arm64.sizeMB + ' MB · Windows ARM64'}
            />
          )}
        </div>
        <div className="mt-3.5 pt-3.5 border-t border-rule-2 text-xs text-muted-2">
          <div className="text-fg-2 font-semibold text-[13px]">Installation — une seule ligne</div>
          <div className="mt-1.5">
            Dans un <strong className="text-fg-2">PowerShell administrateur</strong> sur la machine à
            surveiller. Rien à remplacer : le script détecte l'architecture, télécharge l'installateur et
            demande le code de provisionnement à l'écran.
          </div>
          <CmdBlock cmd={cmdWindows} />

          <div className="mt-3 text-fg-2 font-semibold text-[13px]">
            Déploiement en masse — code fourni, aucune question
          </div>
          <CmdBlock cmd={cmdWindowsMass} />
          <div className="mt-1.5">
            La forme <span className="font-mono">scriptblock</span> est nécessaire pour passer des
            paramètres : <span className="font-mono">irm … | iex</span> ne transmet pas d'arguments.
            Ajouter <span className="font-mono">-NonInteractive</span> pour garantir qu'aucune question
            ne sera posée (tâche planifiée), et <span className="font-mono">-Path</span> pour changer le
            dossier d'installation.
          </div>

          <div className="mt-3 text-fg-2 font-semibold text-[13px]">
            Sans accès réseau à la plateforme depuis la machine cible
          </div>
          <div className="mt-1.5">
            Télécharger l'installateur ci-dessus, le copier sur la machine, puis depuis une invite de
            commandes administrateur :
          </div>
          <CmdBlock cmd={cmdWindowsExe} />
          <div className="mt-1.5">
            <span className="text-fg-2 font-mono">start /wait</span> n'est pas décoratif : l'installateur est
            une application du sous-système Windows, donc <span className="font-mono">cmd.exe</span> ne
            l'attend pas et <span className="font-mono">%ERRORLEVEL%</span> serait celui de{' '}
            <span className="font-mono">start</span>, pas celui de l'installation. Pour l'ARM64, remplacer{' '}
            <span className="text-fg-2 font-mono">-x64</span> par{' '}
            <span className="text-fg-2 font-mono">-arm64</span> dans le nom du fichier.
          </div>

          <div className="mt-3">
            Codes de sortie : <span className="text-fg-2">0</span> succès ·{' '}
            <span className="text-fg-2">1</span> arguments invalides ·{' '}
            <span className="text-fg-2">2</span> non élevé · <span className="text-fg-2">3</span> échec du
            téléchargement · <span className="text-fg-2">4</span> échec de création du service ·{' '}
            <span className="text-fg-2">5</span> service créé mais pas démarré ·{' '}
            <span className="text-fg-2">9</span> erreur inattendue.
          </div>
          <div className="mt-1.5">
            Journal détaillé de l'installation :{' '}
            <span className="font-mono text-fg-2">%TEMP%\FonxtioAgent-install.log</span> (UTF-8) — utile
            quand la console est perdue (tâche planifiée, déploiement de masse).
          </div>
          <div className="mt-1.5">
            Le bouton <span className="text-fg-2">terminal</span> de la page Provisionnement copie la
            commande avec le code déjà rempli.
          </div>
        </div>
      </div>

      <div className={cardCls}>
        {cardTitle("Agent Linux (Debian, Ubuntu, CentOS, AlmaLinux)")}
        {hasLinuxAgent ? (
          <>
            <div className="text-xs text-muted-2">
              Une seule commande, à lancer en root sur la machine cible. Rien à remplacer : le script
              détecte la distribution et l'architecture (x86_64 / aarch64), demande le code de
              provisionnement à l'écran, télécharge le bon paquet, installe le service systemd{' '}
              <span className="font-mono text-fg-2">fonxtio-agent</span> et le démarre.
            </div>
            <CmdBlock cmd={cmdLinux} />

            <div className="mt-3 text-fg-2 font-semibold text-[13px]">
              Déploiement en masse — code fourni, aucune question
            </div>
            <CmdBlock cmd={cmdLinuxMass} />
            <div className="text-xs text-muted-2 mt-1.5">
              Le code de provisionnement rattache la machine au bon client et au bon site. Sans code (ni{' '}
              <span className="font-mono">--site</span>), la plateforme refuse l'enregistrement plutôt
              que d'attribuer la machine arbitrairement. Sans terminal (Ansible, cron, pipeline), la
              question n'est pas posée : le script poursuit et avertit.
            </div>

            <div className="mt-3 text-fg-2 font-semibold text-[13px]">Ce que l'agent installe</div>
            <div className="text-xs text-muted-2 mt-1.5">
              Binaires dans <span className="font-mono text-fg-2">/opt/fonxtio-agent</span> · configuration
              dans <span className="font-mono text-fg-2">/etc/fonxtio-agent</span> · journaux dans{' '}
              <span className="font-mono text-fg-2">/var/log/fonxtio-agent</span> (et{' '}
              <span className="font-mono">journalctl -u fonxtio-agent</span>). Le service tourne en root :
              il lui faut lire le numéro de série DMI, exécuter apt/dnf et redémarrer la machine. La
              configuration vit hors du répertoire d'installation pour survivre aux mises à jour
              automatiques.
            </div>

            <div className="mt-3 text-fg-2 font-semibold text-[13px]">Options utiles</div>
            <div className="text-xs text-muted-2 mt-1.5">
              <span className="font-mono text-fg-2">--site SITE_ID</span> à la place d'un code ·{' '}
              <span className="font-mono text-fg-2">--no-start</span> pour installer sans démarrer ·{' '}
              <span className="font-mono text-fg-2">--keep-config</span> pour une mise à jour sur place ·{' '}
              <span className="font-mono text-fg-2">--install-dir</span> ·{' '}
              <span className="font-mono text-fg-2">--url</span> quand la machine joint la plateforme par
              un autre nom d'hôte ·{' '}
              <span className="font-mono text-fg-2">--help</span>.
            </div>
            <div className="text-xs text-muted-2 mt-1.5">
              Codes de sortie : <span className="text-fg-2">0</span> succès ·{' '}
              <span className="text-fg-2">1</span> arguments invalides · <span className="text-fg-2">2</span>{' '}
              non root · <span className="text-fg-2">3</span> échec du téléchargement ·{' '}
              <span className="text-fg-2">4</span> échec de création du service ·{' '}
              <span className="text-fg-2">5</span> service créé mais pas démarré ·{' '}
              <span className="text-fg-2">6</span> dépendance manquante (systemd, tar, curl) ·{' '}
              <span className="text-fg-2">9</span> erreur inattendue.
            </div>

            <div className="mt-3 text-fg-2 font-semibold text-[13px]">Désinstallation</div>
            <CmdBlock cmd={cmdLinuxUninstall} />

            <div className="flex gap-3.5 flex-wrap items-center mt-3.5">
              {linuxPackages['linux-x64'] && (
                <DownloadBtn
                  label="Paquet x86_64"
                  href={'/updates/' + linuxPackages['linux-x64'].fileName}
                  sub={linuxPackages['linux-x64'].sizeMB + ' MB · tar.gz autonome'}
                />
              )}
              {linuxPackages['linux-arm64'] && (
                <DownloadBtn
                  label="Paquet aarch64"
                  href={'/updates/' + linuxPackages['linux-arm64'].fileName}
                  sub={linuxPackages['linux-arm64'].sizeMB + ' MB · tar.gz autonome'}
                />
              )}
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-2">
            Aucun paquet Linux publié. Le générer depuis le dépôt :{' '}
            <span className="font-mono text-fg-2">bash AgentLinux/Publish-Linux-Agent.sh</span> — le script
            compile les deux architectures, écrit les archives dans{' '}
            <span className="font-mono">wwwroot/updates/</span> et publie l'installateur.
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className={cardCls}>
          {cardTitle('Plateforme')}
          <Row label="Nom" value={p.name} />
          <Row label="Version" value={'v' + p.version} />
          <Row label="Framework" value={p.framework} />
          <Row label="Système" value={p.os} />
        </div>

        <div className={cardCls}>
          {cardTitle('Base de données')}
          <Row label="Type" value={db.type} color="rgb(34 211 238)" />
          <Row label="Version" value={db.version} />
          <Row label="Provider" value={db.provider} />
          <Row label="Inventaires stockés" value={c.inventories.toLocaleString()} />
          <Row label="Journaux d'audit" value={c.auditLogs.toLocaleString()} />
        </div>

        <div className={cardCls}>
          {cardTitle('Données')}
          <Row label="Clients" value={c.clients} />
          <Row label="Sites" value={c.sites} />
          <Row label="Agents" value={c.agents} />
          <Row
            label="Agents en ligne"
            value={c.onlineAgents}
            color={c.onlineAgents > 0 ? '#22c55e' : '#f87171'}
          />
          <Row label="Utilisateurs" value={c.users} />
          <Row label="Commandes exécutées" value={c.commands.toLocaleString()} />
          <Row label="Alertes totales" value={c.alerts.toLocaleString()} />
          <Row
            label="Alertes non résolues"
            value={c.unresolvedAlerts}
            color={c.unresolvedAlerts > 0 ? '#f59e0b' : '#22c55e'}
          />
        </div>

        <div className={cardCls}>
          {cardTitle("Points d'accès")}
          <div className="border-b border-rule-2 flex justify-between items-center py-1.5">
            <span className="text-muted text-[13px]">API Documentation</span>
            <a href={ep.api} target="_blank" className="text-blue-400 text-[13px] font-mono no-underline">
              {ep.api}
            </a>
          </div>
          <div className="border-b border-rule-2 flex justify-between items-center py-1.5">
            <span className="text-muted text-[13px]">SignalR Hub</span>
            <code className="text-[13px] text-cyan-400">{ep.signalr}</code>
          </div>
        </div>

        <div className={cardCls}>
          {cardTitle('Informations')}
          <Row label="Agent API" value="/api/agent/*" />
          <Row label="Platform API" value="/api/platform/*" />
        </div>
      </div>
    </div>
  );
}
